/**
 * @file tipPendingOAuth.js
 * @description JSAPI 打赏 OAuth 跳转前暂存上下文，回站后自动续打赏
 * @module features/comic/utils/tipPendingOAuth
 */

const PENDING_TIP_STORAGE_KEY = 'comic:pendingTip'
const PENDING_TIP_RESUME_DONE_KEY = 'comic:tipResumeDoneId'

/**
 * @typedef {Object} PendingTipResumeContext
 * @property {string} comicId
 * @property {string} [comicTitle]
 * @property {'detail' | 'reader'} source
 * @property {string} [volId]
 * @property {string} tipCode
 * @property {string} [idempotencyKey]
 * @property {string} [resumeSessionId] 本次 OAuth 待续会话 ID（防双次 resume）
 * @property {string} [expectedUserId] OAuth 前登录用户 ID
 * @property {string} [expectedUserPhone] OAuth 前登录用户手机号
 * @property {Record<string, unknown>} [preOAuthSession] OAuth 前完整本地会话（回站校验失败时恢复）
 */

/**
 * @returns {string}
 */
function createResumeSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `tip-resume-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * OAuth 跳转前保存待续打赏信息
 * @param {PendingTipResumeContext} context
 */
export function savePendingTipResume(context) {
  if (typeof sessionStorage === 'undefined') return
  const resumeSessionId =
    typeof context.resumeSessionId === 'string' && context.resumeSessionId.trim()
      ? context.resumeSessionId.trim()
      : createResumeSessionId()
  sessionStorage.setItem(
    PENDING_TIP_STORAGE_KEY,
    JSON.stringify({ ...context, resumeSessionId })
  )
}

/**
 * 清除待续打赏标记（预下单成功等场景）
 */
export function clearPendingTipResume() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(PENDING_TIP_STORAGE_KEY)
  sessionStorage.removeItem(PENDING_TIP_RESUME_DONE_KEY)
}

/**
 * @param {string} sessionId
 * @returns {boolean}
 */
export function isTipResumeSessionDone(sessionId) {
  if (typeof sessionStorage === 'undefined' || !sessionId?.trim()) return false
  return sessionStorage.getItem(PENDING_TIP_RESUME_DONE_KEY) === sessionId.trim()
}

/**
 * @param {string} sessionId
 */
export function markTipResumeSessionDone(sessionId) {
  if (typeof sessionStorage === 'undefined' || !sessionId?.trim()) return
  sessionStorage.setItem(PENDING_TIP_RESUME_DONE_KEY, sessionId.trim())
}

/**
 * 读取待续打赏信息（不删除）
 * @returns {PendingTipResumeContext | null}
 */
export function peekPendingTipResume() {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(PENDING_TIP_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.comicId || !parsed.tipCode) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * 读取并清除待续打赏信息
 * @returns {PendingTipResumeContext | null}
 */
export function consumePendingTipResume() {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(PENDING_TIP_STORAGE_KEY)
  if (!raw) return null
  sessionStorage.removeItem(PENDING_TIP_STORAGE_KEY)
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.comicId || !parsed.tipCode) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}
